import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/api/http";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Search, Wrench } from "lucide-react";
import { moneyBRL } from "@/utils/money";

type OStatus = "aberta" | "em_execucao" | "aguardando_pecas" | "finalizada" | "faturada" | "cancelada";

type OSRow = {
  id: string;
  status: OStatus;
  total: any;
  createdAt: string;
  updatedAt: string;
  cliente?: { id: string; nome: string; telefone?: string | null; documento?: string | null };
  veiculo?: { id: string; placa?: string | null; marca?: string | null; modelo?: string | null; ano?: number | null; km?: any };
  _count?: { itens: number };
};

function statusBadge(status: OStatus) {
  const base = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border";
  switch (status) {
    case "aberta":
      return <span className={`${base} bg-slate-50 text-slate-700 border-slate-200`}>Aberta</span>;
    case "em_execucao":
      return <span className={`${base} bg-blue-50 text-blue-700 border-blue-200`}>Em execução</span>;
    case "aguardando_pecas":
      return <span className={`${base} bg-amber-50 text-amber-800 border-amber-200`}>Aguardando peças</span>;
    case "finalizada":
      return <span className={`${base} bg-emerald-50 text-emerald-700 border-emerald-200`}>Finalizada</span>;
    case "faturada":
      return <span className={`${base} bg-purple-50 text-purple-700 border-purple-200`}>Faturada</span>;
    case "cancelada":
      return <span className={`${base} bg-red-50 text-red-700 border-red-200`}>Cancelada</span>;
    default:
      return <span className={`${base} bg-slate-50 text-slate-700 border-slate-200`}>{status}</span>;
  }
}

export default function OSList() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<OSRow[]>([]);
  const [meta, setMeta] = useState<any>(null);

  const q = sp.get("q") ?? "";
  const status = sp.get("status") ?? "";
  const dateFrom = sp.get("dateFrom") ?? "";
  const dateTo = sp.get("dateTo") ?? "";
  const page = Number(sp.get("page") ?? "1");
  const limit = Number(sp.get("limit") ?? "20");

  const [qInput, setQInput] = useState(q);
  const [statusInput, setStatusInput] = useState(status);
  const [fromInput, setFromInput] = useState(dateFrom);
  const [toInput, setToInput] = useState(dateTo);

  const queryParams = useMemo(() => {
    const params: any = { page, limit };
    if (q) params.q = q;
    if (status) params.status = status;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    return params;
  }, [q, status, dateFrom, dateTo, page, limit]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/api/os", { params: queryParams });
      setRows(data?.data ?? []);
      setMeta(data?.meta ?? null);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao carregar OS");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, dateFrom, dateTo, page, limit]);

  function applyFilters(e?: any) {
    e?.preventDefault?.();
    const next = new URLSearchParams(sp);
    qInput.trim() ? next.set("q", qInput.trim()) : next.delete("q");
    statusInput ? next.set("status", statusInput) : next.delete("status");
    fromInput ? next.set("dateFrom", fromInput) : next.delete("dateFrom");
    toInput ? next.set("dateTo", toInput) : next.delete("dateTo");
    next.set("page", "1");
    setSp(next);
  }

  function clearFilters() {
    setQInput("");
    setStatusInput("");
    setFromInput("");
    setToInput("");
    setSp(new URLSearchParams({ page: "1", limit: String(limit) }));
  }

  function goPage(p: number) {
    const next = new URLSearchParams(sp);
    next.set("page", String(p));
    setSp(next);
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Wrench size={20} />
              Ordens de Serviço
            </h1>
            <p className="text-sm text-slate-500">Crie, acompanhe e finalize OS com itens de serviço e peças.</p>
          </div>

          <Link to="/os/nova">
            <Button className="gap-2">
              <Plus size={16} />
              Nova OS
            </Button>
          </Link>
        </div>

        {/* Filtros */}
        <form onSubmit={applyFilters} className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5">
              <Input
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Buscar por cliente, placa, modelo, telefone, diagnóstico..."
              />
            </div>

            <div className="md:col-span-3">
              <select
                value={statusInput}
                onChange={(e) => setStatusInput(e.target.value)}
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                <option value="">Todos status</option>
                <option value="aberta">Aberta</option>
                <option value="em_execucao">Em execução</option>
                <option value="aguardando_pecas">Aguardando peças</option>
                <option value="finalizada">Finalizada</option>
                <option value="faturada">Faturada</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <Input type="date" value={fromInput} onChange={(e) => setFromInput(e.target.value)} />
            </div>

            <div className="md:col-span-2">
              <Input type="date" value={toInput} onChange={(e) => setToInput(e.target.value)} />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={clearFilters}>
              Limpar
            </Button>
            <Button type="submit" className="gap-2">
              <Search size={16} />
              Filtrar
            </Button>
          </div>
        </form>

        {/* Lista */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-14 flex items-center justify-center text-slate-500 gap-2">
              <Loader2 className="animate-spin" size={18} />
              Carregando...
            </div>
          ) : rows.length === 0 ? (
            <div className="py-14 text-center text-slate-500">
              Nenhuma OS encontrada.
            </div>
          ) : (
            <div className="divide-y">
              {rows.map((os) => {
                const cliente = os.cliente?.nome ?? "—";
                const placa = os.veiculo?.placa ? ` • ${os.veiculo.placa}` : "";
                const modelo = os.veiculo?.modelo ? ` • ${os.veiculo.marca ?? ""} ${os.veiculo.modelo}`.trim() : "";
                const itensCount = os._count?.itens ?? 0;

                return (
                  <button
                    key={os.id}
                    onClick={() => navigate(`/os/${os.id}`)}
                    className="w-full text-left p-4 hover:bg-slate-50 transition flex items-start justify-between gap-4"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">OS {os.id.slice(-6).toUpperCase()}</span>
                        {statusBadge(os.status)}
                        <span className="text-xs text-slate-500">
                          Itens: <b className="text-slate-700">{itensCount}</b>
                        </span>
                      </div>

                      <div className="text-sm text-slate-700">
                        <b>{cliente}</b>
                        <span className="text-slate-500">{placa}{modelo}</span>
                      </div>

                      <div className="text-xs text-slate-500">
                        Criada em: {new Date(os.createdAt).toLocaleString("pt-BR")}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-sm text-slate-500">Total</div>
                      <div className="text-lg font-bold text-slate-900">{moneyBRL(os.total)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Paginação */}
        {meta?.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500">
              Página <b className="text-slate-800">{meta.page}</b> de{" "}
              <b className="text-slate-800">{meta.totalPages}</b> • Total:{" "}
              <b className="text-slate-800">{meta.total}</b>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" disabled={meta.page <= 1} onClick={() => goPage(meta.page - 1)}>
                Anterior
              </Button>
              <Button variant="outline" disabled={meta.page >= meta.totalPages} onClick={() => goPage(meta.page + 1)}>
                Próxima
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
